import {
  customFieldsListOptions,
  EntityTypeEnum,
  leadsLogsListQueryKey,
  leadsOpportunitiesListQueryKey,
  leadsRetrieveOptions,
  opportunitiesCreateMutation,
  opportunitiesListQueryKey,
  opportunitiesLogsListQueryKey,
  opportunitiesPartialUpdateMutation,
  opportunitiesPipelineListQueryKey,
  opportunitiesPipelinesListOptions,
  opportunitiesRetrieveOptions,
  opportunitiesRetrieveQueryKey,
  ReturnOpportunityReadable,
  Type83fEnum
} from '@crm/api'
import { OpportunityForm } from '@crm/components'
import { useAppNavigate } from '@crm/hooks/useAppNavigate'
import { paths } from '@crm/routes'
import {
  defaultNonPagination,
  formatDateToDayjs,
  getSelectIdValue,
  getUserFullName,
  transformCustomFieldValues,
  transformCustomFieldValuesToForm
} from '@crm/utils'
import {
  useInvalidatePredicateQueries,
  useQueryUpdate,
  useSessionContext
} from '@shared/hooks'
import { DEFAULT_ISO_DATE_FORMAT, format, showApiErrors } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { App, Button, Card, Flex, Form } from 'antd'
import { AxiosError } from 'axios'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { useStyle } from '../styles'

const formName = 'add-edit-opportunity-form'

type Props = {
  opportunityDetails?: ReturnOpportunityReadable
  onCancelModal?: () => void
}

export const OpportunitiesCreatePage = ({
  opportunityDetails,
  onCancelModal
}: Props) => {
  const { t } = useTranslation()
  const { user } = useSessionContext()
  const { opportunityId, id } = useParams()
  const { styles } = useStyle()
  const { params } = useQueryUpdate()
  const { invalidateQueries } = useInvalidatePredicateQueries()
  const { notification } = App.useApp()

  const appNavigate = useAppNavigate()
  const navigate = useNavigate()
  const [form] = Form.useForm()

  const { data: opportunitiesData, isLoading: isOpportunitiesLoading } =
    useQuery({
      ...opportunitiesRetrieveOptions({ path: { id: Number(opportunityId) } }),
      enabled: !!opportunityId
    })

  const opportunityData = opportunityDetails ?? opportunitiesData
  const leadId = opportunityData?.lead?.id ?? id ?? params.leadId
  const clientId = opportunityData?.client?.id

  const { data: lead, isLoading: isLeadsLoading } = useQuery({
    ...leadsRetrieveOptions({ path: { id: Number(leadId) } }),
    enabled: !!leadId
  })

  const { data: customFields, isLoading: isCustomFieldsLoading } = useQuery(
    customFieldsListOptions({
      query: {
        entity_type: [EntityTypeEnum.OPPORTUNITY],
        ...defaultNonPagination
      }
    })
  )

  const { data: opportunityPipelineData } = useQuery({
    ...opportunitiesPipelinesListOptions({
      query: { page: 1, per_page: 1, ordering: '-is_default' }
    })
  })

  const { filteredCustomFields, checkboxCustomFields } = useMemo(() => {
    const availableCustomFields =
      customFields?.results?.filter((e) => !e.deleted_at) || []

    const grouped = Object.groupBy(availableCustomFields, (e) =>
      e.type === Type83fEnum.BOOLEAN
        ? 'checkboxCustomFields'
        : 'filteredCustomFields'
    )

    return {
      filteredCustomFields: grouped.filteredCustomFields || [],
      checkboxCustomFields: grouped.checkboxCustomFields || []
    }
  }, [customFields?.results])

  useEffect(() => {
    const pipelineResults = opportunityPipelineData?.results?.[0]
    const defaultPipeline = pipelineResults?.is_default
      ? pipelineResults
      : undefined

    if (opportunityData) {
      const {
        owner,
        close_date,
        contact,
        status,
        tags,
        product,
        client,
        currency,
        ...formData
      } = opportunityData

      const customFieldFormData = transformCustomFieldValuesToForm(
        opportunityData?.custom_field_values || []
      )
      Object.assign(formData, {
        custom_field_values: customFieldFormData || undefined
      })

      form.setFieldsValue({
        ...formData,
        currency: currency?.id,
        contact: contact?.id,
        owner: owner?.name ?? getUserFullName(user?.user),
        status: status?.id,
        close_date: close_date && formatDateToDayjs(close_date),
        client: client?.id,
        tags: tags?.map((tag) => tag?.id),
        lead: lead?.name,
        pipeline: opportunityData?.status?.pipeline?.id ?? defaultPipeline?.id,
        product: product?.id
      })
    } else {
      form.setFieldsValue({
        lead: lead?.name,
        contact: lead?.contact?.id,
        owner: defaultPipeline?.responsible?.name,
        pipeline: defaultPipeline?.id
      })
    }
  }, [opportunityData, form, user, lead, customFields, opportunityPipelineData])

  const onMutate = {
    onError: (e: AxiosError) => showApiErrors(e, form),
    onSuccess: async (opportunity: ReturnOpportunityReadable) => {
      await invalidateQueries([
        leadsOpportunitiesListQueryKey({ path: { lead_id: String(leadId) } }),
        leadsLogsListQueryKey({ path: { lead_id: String(leadId) } }),
        opportunitiesListQueryKey(),
        opportunitiesPipelineListQueryKey({
          query: {
            status: [opportunity?.status?.id]
          }
        }),
        opportunitiesRetrieveQueryKey({ path: { id: Number(id) } }),
        opportunitiesLogsListQueryKey({ path: { opportunity_id: String(id) } })
      ])

      notification.success({
        message: t('success.success'),
        description: t('success.success_data_save')
      })

      if (onCancelModal) {
        return onCancelModal()
      } else {
        return opportunityId
          ? navigate(-1)
          : appNavigate(
              paths.opportunitiesDetails.root(
                String(opportunity?.id),
                String(opportunity?.lead?.id)
              )
            )
      }
    }
  }

  const { mutate: createMutate, isPending: isCreatePending } = useMutation({
    ...opportunitiesCreateMutation(),
    ...onMutate
  })

  const { mutate: editMutate, isPending: isEditPending } = useMutation({
    ...opportunitiesPartialUpdateMutation(),
    ...onMutate
  })

  const onSubmit = async () => {
    const values = await form.validateFields()

    const {
      close_date,
      product,
      owner,
      lead,
      client: _client,
      ...rest
    } = values

    const transformedCustomFields = customFields?.results
      ? transformCustomFieldValues(
          rest?.custom_field_values,
          customFields.results
        )
      : []

    const requestBody = {
      ...rest,
      close_date: close_date && format(close_date, DEFAULT_ISO_DATE_FORMAT),
      owner: getSelectIdValue(
        owner,
        (opportunityData?.owner?.id || user?.user.sso_id) ?? undefined
      ),
      product: getSelectIdValue(product, opportunityData?.product?.id),
      custom_field_values: transformedCustomFields
    }

    if (opportunityData) {
      // lead/client associations are immutable on edit; sending both
      // triggers a 400 ("Assign either a lead or a client, not both.")
      editMutate({ path: { id: opportunityData.id }, body: requestBody })
    } else {
      createMutate({
        body: {
          ...requestBody,
          lead: leadId || getSelectIdValue(lead, opportunityData?.lead?.id)
        }
      })
    }
  }

  const isLoading =
    isOpportunitiesLoading || isLeadsLoading || isCustomFieldsLoading
  const isPending = isCreatePending || isEditPending

  const title = id
    ? t('opportunities.edit_opportunity')
    : t('opportunities.add_opportunity')

  return (
    <Card
      className={styles.leadCard}
      variant={opportunityDetails ? 'borderless' : undefined}
      title={opportunityDetails ? null : title}
      loading={isLoading}
      actions={[
        <Flex justify="end" key="actions" gap={12}>
          <Button
            disabled={isPending}
            onClick={() => (onCancelModal ? onCancelModal() : navigate(-1))}
          >
            {t('buttons.cancel')}
          </Button>
          <Button
            type="primary"
            disabled={isPending}
            loading={isPending}
            onClick={() => onSubmit()}
          >
            {opportunityId ? t('buttons.edit') : t('buttons.save')}
          </Button>
        </Flex>
      ]}
    >
      <Form name={formName} form={form} onFinish={onSubmit} layout="vertical">
        <OpportunityForm
          form={form}
          opportunityData={opportunityData}
          checkboxCustomFields={checkboxCustomFields}
          filteredCustomFields={filteredCustomFields}
          leadId={leadId}
          clientId={clientId}
        />
      </Form>
    </Card>
  )
}
